import { useEffect, useRef, useState } from "react";
import { api, getUser } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Upload, Loader2, Trash2, Files } from "lucide-react";
import { toast } from "sonner";
import { ACCEPT, KIND_LABEL, MAX_BYTES, uploadMessage } from "@/lib/files";
import { FilePreviewDialog } from "@/components/FilePreviewDialog";
import { CustomerFileRow, DeleteFileDialog, TrashRow } from "@/components/KundeDateiRows";

const WRITE_ROLES = ["admin", "manager", "employee", "buchhaltung", "versand", "rechnung_pdf"];
const TRASH_ROLES = ["admin", "manager"];

function CustomerUpload({ kundeId, onUploaded }) {
  const [kind, setKind] = useState("kundendatei");
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const input = useRef(null);
  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    for (const file of files) {
      if (file.size > MAX_BYTES) { toast.error(`${file.name}: Datei überschreitet 100 MB.`); continue; }
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      try {
        const r = await api.post(`/kunden/${kundeId}/dateien`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(uploadMessage(file.name, r.data));
      } catch (e) { toast.error(`${file.name}: ${e?.response?.data?.detail || "Fehlgeschlagen."}`); }
    }
    setBusy(false);
    if (input.current) input.current.value = "";
    onUploaded();
  };
  return (
    <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={(e) => { e.preventDefault(); setOver(false); upload(Array.from(e.dataTransfer.files || [])); }}
      className={`border-2 border-dashed rounded-lg p-4 flex items-center gap-3 flex-wrap transition-colors ${over ? "border-blue-500 bg-blue-50" : "border-slate-300 bg-slate-50"}`} data-testid="kunde-dateien-dropzone">
      <Upload className="w-6 h-6 text-slate-400" />
      <div className="flex-1 min-w-[180px]">
        <p className="text-sm text-slate-700">Dateien direkt beim Kunden ablegen</p>
        <p className="text-xs text-slate-500">STL · STEP · 3MF · OBJ · PDF · JPG · PNG · GLTF · GLB · max. 100 MB</p>
      </div>
      <Select value={kind} onValueChange={setKind}>
        <SelectTrigger className="w-40" aria-label="Datei-Rolle" data-testid="kunde-dateien-kind-select"><SelectValue /></SelectTrigger>
        <SelectContent>{Object.entries(KIND_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
      </Select>
      <input ref={input} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => upload(Array.from(e.target.files || []))} data-testid="kunde-dateien-file-input" />
      <Button size="sm" onClick={() => input.current?.click()} disabled={busy} data-testid="kunde-dateien-upload-button">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Lädt hoch…</> : "Datei auswählen"}
      </Button>
    </div>
  );
}

// Tab „Dateien“ im Kunden: eine Zeile je Datei (alle Versionen), Filter „Papierkorb“ (90 Tage wiederherstellbar)
export function KundeDateienTab({ kundeId }) {
  const role = getUser()?.rolle;
  const [view, setView] = useState("dateien");
  const [items, setItems] = useState(null);
  const [preview, setPreview] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const trash = view === "papierkorb";
  const load = () => api.get(`/kunden/${kundeId}/dateien${trash ? "/papierkorb" : ""}`)
    .then((r) => setItems(r.data)).catch(() => { setItems([]); toast.error("Dateien konnten nicht geladen werden."); });
  useEffect(() => { setItems(null); load(); }, [kundeId, view]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-3" data-testid="kunde-dateien-tab">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="text-base font-semibold text-slate-900">{trash ? "Papierkorb" : "Dateien des Kunden"}</h3>
        <Badge variant="outline" className="bg-slate-100 text-slate-700 border-slate-300" data-testid="kunde-dateien-count">{items ? items.length : "…"}</Badge>
        <ToggleGroup type="single" value={view} onValueChange={(v) => v && setView(v)} className="ml-auto" aria-label="Ansicht">
          <ToggleGroupItem value="dateien" size="sm" data-testid="kunde-dateien-filter-dateien"><Files className="w-4 h-4 mr-1" /> Dateien</ToggleGroupItem>
          <ToggleGroupItem value="papierkorb" size="sm" data-testid="kunde-dateien-filter-papierkorb"><Trash2 className="w-4 h-4 mr-1" /> Papierkorb</ToggleGroupItem>
        </ToggleGroup>
      </div>
      {trash && <p className="text-xs text-slate-500">Gelöschte Dateien bleiben 90 Tage im Papierkorb und werden danach endgültig gelöscht. Beim Wiederherstellen werden auch die früheren Auftragsverknüpfungen wiederhergestellt.</p>}
      {!trash && WRITE_ROLES.includes(role) && <CustomerUpload kundeId={kundeId} onUploaded={load} />}
      {items === null ? (
        <div className="py-6 flex items-center justify-center text-sm text-slate-500"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Lädt…</div>
      ) : items.length === 0 ? (
        <p className="text-sm text-slate-500 italic py-2" data-testid="kunde-dateien-empty">{trash ? "Der Papierkorb ist leer." : "Noch keine Dateien."}</p>
      ) : (
        <ul className="space-y-2">
          {trash
            ? items.map((f) => <TrashRow key={f.trash_id} f={f} kundeId={kundeId} canRestore={TRASH_ROLES.includes(role)} onPreview={setPreview} onRestored={load} />)
            : items.map((f) => <CustomerFileRow key={f.family_id} f={f} canDelete={TRASH_ROLES.includes(role)} onPreview={setPreview} onDelete={setToDelete} />)}
        </ul>
      )}
      <FilePreviewDialog att={preview} onClose={() => setPreview(null)} />
      <DeleteFileDialog file={toDelete} kundeId={kundeId} onClose={() => setToDelete(null)} onDeleted={load} />
    </div>
  );
}
